import { useEffect, useRef, useState } from 'react';
import { InformationCircle } from 'assets/icons';
import Tooltip from 'components/tooltips/Tooltips';
import Usefetchdata from 'hooks/usefetchdata';
import { isSuperAdmin } from 'helper/roles';
import { toast } from 'react-toastify';
import UseContext from '../../../../../hooks/useContext';
import ApiDatabase from '../../../../../server';
import Button from '../../../../button';
import StyledInput from '../../../../styledInput/StyledInput';
import ModalBody from '../../../components/body';
import ModalFooter from '../../../components/footer';
import JustificatifConfirmation, {
  CONFIRMATION_TITLE,
} from 'components/justificatifConfirmation/JustificatifConfirmation';
import useJustificatifConfirmation from 'hooks/useJustificatifConfirmation';
import useBaseline from 'hooks/useBaseline';
import Modalheader from '../../../components/header';
import useModal from '../../../useModal';

// Defaults from the Figma, applied only to a ticket that was never set up: a
// neutral billing coefficient, the BeSTT day counter restaurant tickets are
// granted on, and the usual modality.
const DEFAULT_COEF = '1.00';
const DEFAULT_UNIT_NAME = 'J>4H';
const DEFAULT_MODALITY_NAME = 'Le salarié est indemnisé du montant des TR';

const findIdByName = (list, name) =>
  list.find((item) => item.label?.trim().toLowerCase() === name.toLowerCase())
    ?.value ?? '';

const fieldError = (field, value) => {
  switch (field) {
    case 'value':
      return !value || Number(value) <= 0
        ? 'La valeur du ticket doit être un montant positif'
        : null;
    case 'employerShare':
      return value === '' || Number(value) < 0 || Number(value) > 100
        ? 'La valeur doit être entre 0 et 100'
        : null;
    case 'coef':
      return value === '' || Number(value) < 0
        ? 'Le coef doit être un nombre positif'
        : null;
    case 'unit':
      return !value ? 'Veuillez choisir une unité' : null;
    case 'modality':
      return !value ? 'Veuillez choisir une modalité' : null;
    default:
      return null;
  }
};

const ModalBonusesAndAllowancesRestaurantTicket = () => {
  const { toggle } = useModal();
  const { modalData, setModalDataReturn } = UseContext();
  const { userData } = Usefetchdata();
  // Both fields decide what the client is billed. Readable by everyone who
  // can open the panel, changeable only by an admin — a collaborator (515)
  // clears the endpoint's role gate without that making it their call.
  const canEditBilling = isSuperAdmin(userData);
  const [isLoading, setIsLoading] = useState(false);
  const justificatif = useJustificatifConfirmation();
  const baseline = useBaseline(null);
  const [isWaitingRestaurantTicket, setIsWaitingRestaurantTicket] =
    useState(true);
  const [isWaitingRestaurantTicketList, setIsWaitingRestaurantTicketList] =
    useState(true);
  const [isWaitingBonusUnitList, setIsWaitingBonusUnitList] = useState(true);
  const [restaurantTicket, setRestaurantTicket] = useState({
    value: '',
    employerShare: '',
    coef: '',
    unit: '',
    modality: '',
  });
  const [errorRestaurantTicket, setErrorRestaurantTicket] = useState({
    value: '',
    employerShare: '',
    coef: '',
    unit: '',
    modality: '',
  });
  const [restaurantTicketModalityList, setRestaurantTicketModalityList] =
    useState([]);
  const [bonusUnitList, setBonusUnitList] = useState([]);
  const hasAppliedDefaults = useRef(false);

  const token = localStorage.getItem('xsrfToken');

  const isWaiting =
    isWaitingRestaurantTicket ||
    isWaitingRestaurantTicketList ||
    isWaitingBonusUnitList;

  // The option label carries the BeSTT code alone; what it counts is spelled
  // out under the field, where it does not have to fit on one dropdown line.
  const selectedUnitDescription = bonusUnitList.find(
    (item) => item.value === restaurantTicket.unit
  )?.description;

  const handleChange = (value, type) => {
    setRestaurantTicket((prev) => ({
      ...prev,
      [type]: value,
    }));

    setErrorRestaurantTicket((prev) => ({
      ...prev,
      [type]: fieldError(type, value),
    }));
  };

  const validateRestaurantTicket = () => {
    const newError = Object.keys(restaurantTicket).reduce(
      (acc, field) => ({
        ...acc,
        [field]: fieldError(field, restaurantTicket[field]),
      }),
      {},
    );

    setErrorRestaurantTicket(newError);

    return !Object.values(newError).some(Boolean);
  };

  const handleSubmit = () => {
    if (!validateRestaurantTicket() || !modalData?.idCompany) return;
    // The ticket's value and employer share reach every contract, so the
    // change carries the client's written request for it.
    if (!baseline.hasChanged(restaurantTicket)) return;
    if (!justificatif.isConfirming) return justificatif.setIsConfirming(true);
    justificatif.confirm(save);
  };

  const save = (storedJustificatif) => {
    if (validateRestaurantTicket() && modalData?.idCompany) {
      setIsLoading(true);
      ApiDatabase.postRestaurantTicketCompany(
        {
          token,
          idCompany: modalData?.idCompany,
          restaurantTicket,
          justificatif: storedJustificatif,
        },
        (data) => {
          setIsLoading(false);
          toast.success('Le ticket restaurant a été mis à jour');
          // The API echoes the saved ticket with the unit and modality labels
          // resolved, so the summary card refreshes without another round trip.
          setModalDataReturn({
            type: 'restaurant_ticket_updated',
            restaurantTicket: data.restaurantTicket,
          });
          toggle();
        },
        (err) => {
          if (err.name !== 'CanceledError') {
            console.log(err);
            setIsLoading(false);
          }
        },
      );
    }
  };

  useEffect(() => {
    if (!modalData?.idCompany) {
      setIsWaitingRestaurantTicket(false);
      setIsWaitingRestaurantTicketList(false);
      setIsWaitingBonusUnitList(false);
      return;
    }

    ApiDatabase.getRestaurantTicketCompany(
      { token, idCompany: modalData.idCompany },
      (data) => {
        const loaded = {
          value: data.restaurantTicket.value ?? '',
          employerShare: data.restaurantTicket.employer_share ?? '',
          coef: data.restaurantTicket.coef ?? '',
          unit: data.restaurantTicket.unit ?? '',
          modality: data.restaurantTicket.modality ?? '',
        };
        setRestaurantTicket(loaded);
        // Captured before the defaults run: on a ticket that has one they
        // change nothing, and on a first ticket the value and the employer
        // share are required anyway, so an untouched form cannot be confirmed.
        baseline.capture(loaded);
        setIsWaitingRestaurantTicket(false);
      },
      (err) => {
        if (err.name !== 'CanceledError') {
          console.error(err);
          setIsWaitingRestaurantTicket(false);
        }
      },
    );

    ApiDatabase.getRestaurantTicketModalityListCompany(
      { token, idCompany: modalData.idCompany },
      (data) => {
        setRestaurantTicketModalityList([
          { value: '', label: '-' },
          ...data.restaurantTicketModalityList.map((item) => ({
            ...item,
            value: item._id,
            label: item.name,
          })),
        ]);
        setIsWaitingRestaurantTicketList(false);
      },
      (err) => {
        if (err.name !== 'CanceledError') {
          console.error(err);
          setIsWaitingRestaurantTicketList(false);
        }
      },
    );

    ApiDatabase.getBonusUnitListCompany(
      { token, idCompany: modalData.idCompany },
      (data) => {
        setBonusUnitList([
          { value: '', label: '-' },
          ...data.bonusUnitList.map((item) => ({
            ...item,
            value: item._id,
            label: item.name,
          })),
        ]);
        setIsWaitingBonusUnitList(false);
      },
      (err) => {
        if (err.name !== 'CanceledError') {
          console.error(err);
          setIsWaitingBonusUnitList(false);
        }
      },
    );
  }, [modalData?.idCompany]);

  // Defaults can only be resolved once both referentials are in: they are
  // matched by name, and the ticket itself always wins over them.
  useEffect(() => {
    if (isWaiting || hasAppliedDefaults.current) return;

    hasAppliedDefaults.current = true;
    setRestaurantTicket((prev) => ({
      ...prev,
      coef: prev.coef === '' ? DEFAULT_COEF : prev.coef,
      unit: prev.unit || findIdByName(bonusUnitList, DEFAULT_UNIT_NAME),
      modality:
        prev.modality ||
        findIdByName(restaurantTicketModalityList, DEFAULT_MODALITY_NAME),
    }));
  }, [isWaiting, bonusUnitList, restaurantTicketModalityList]);

  return (
    <>
      <Modalheader hide={toggle} hideShow={true}>
        {justificatif.isConfirming ? CONFIRMATION_TITLE : 'Ticket restaurant'}
      </Modalheader>
      <ModalBody>
        {justificatif.isConfirming ? (
          <JustificatifConfirmation
            file={justificatif.file}
            setFile={justificatif.setFile}
            isAttested={justificatif.isAttested}
            setIsAttested={justificatif.setIsAttested}
            error={justificatif.error}
            description={
              'La valeur du ticket et la part patronale sont reportées sur les contrats. Un justificatif est requis avant validation.'
            }
          />
        ) : (
          <>
        <div className="flex flex-col gap-2.5 text-sm">
          {isWaiting ? (
            <div>
              <div className="flex flex-col gap-2">
                <div className={'h-16 skeleton-bar w-full'}></div>
                <div className={'h-16 skeleton-bar w-full'}></div>
                <div className={'h-16 skeleton-bar w-full'}></div>
              </div>
            </div>
          ) : (
            <>
              <div>
                <StyledInput
                  type="number"
                  label="Valeur du ticket"
                  value={restaurantTicket.value}
                  unit={'€'}
                  step={'0.01'}
                  onChange={(e) => handleChange(e.target.value, 'value')}
                  error={errorRestaurantTicket.value}
                />
              </div>
              <div>
                <StyledInput
                  type="number"
                  label="Part patronale"
                  value={restaurantTicket.employerShare}
                  unit={'%'}
                  onChange={(e) =>
                    handleChange(e.target.value, 'employerShare')
                  }
                  error={errorRestaurantTicket.employerShare}
                />
              </div>
              <div className={'flex gap-2.5'}>
                <div className={'flex-1'}>
                  {/* Every ticket in production bills at 1. The field stays
                      visible because it is what BeSTT receives; an admin can
                      still change it, nobody else. */}
                  <StyledInput
                    type="number"
                    label={
                      <span
                        className={
                          'flex items-center justify-between gap-1 w-full'
                        }
                      >
                        Coef de facturation
                        {/* Only where it is true: an admin can change the
                            field, so telling them to contact one would be
                            wrong. The modal shell sits at z-50, hence 60. */}
                        {!canEditBilling && (
                          <Tooltip
                            text={
                              'Contacter l’admin pour modifier le coefficient'
                            }
                            placement={'top'}
                            zIndex={60}
                          >
                            <span className={'cursor-help'}>
                              <InformationCircle wh={20} color={'#9CA3AF'} />
                            </span>
                          </Tooltip>
                        )}
                      </span>
                    }
                    value={restaurantTicket.coef}
                    step={'0.01'}
                    isDisabled={!canEditBilling}
                    onChange={(e) => handleChange(e.target.value, 'coef')}
                    error={errorRestaurantTicket.coef}
                  />
                </div>
                <div className={'flex-1'}>
                  <StyledInput
                    type="select"
                    label="Unité"
                    value={restaurantTicket.unit}
                    list={bonusUnitList}
                    isSearchable={true}
                    onChange={(e) => handleChange(e.target.value, 'unit')}
                    error={errorRestaurantTicket.unit}
                  />
                  {selectedUnitDescription && (
                    <div className={'text-gray-500 mt-1'}>
                      {selectedUnitDescription}
                    </div>
                  )}
                </div>
              </div>
              <div>
                {/* Locked like the coefficient, and for the same reason. The
                    design carries no icon here: one explanation for the pair
                    is enough, and it sits on the field that raises it. */}
                <StyledInput
                  type="select"
                  label="Modalité"
                  value={restaurantTicket.modality}
                  list={restaurantTicketModalityList}
                  isDisabled={!canEditBilling}
                  onChange={(e) => handleChange(e.target.value, 'modality')}
                  error={errorRestaurantTicket.modality}
                />
              </div>
            </>
          )}
        </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {isLoading || justificatif.isUploading ? (
          <>
            <Button size={'LG'} color={'SECONDARY'}>
              Annuler
            </Button>
            <Button size={'LG'} color={'DISABLED'}>
              Confirmation ...
            </Button>
          </>
        ) : (
          <>
            <Button
              size={'LG'}
              color={'SECONDARY'}
              // Backs out of the confirmation, not the modal: the values being
              // confirmed are still worth seeing.
              onClick={
                justificatif.isConfirming
                  ? () => justificatif.setIsConfirming(false)
                  : toggle
              }
            >
              Annuler
            </Button>
            <Button
              size={'LG'}
              color={
                !baseline.hasChanged(restaurantTicket) ||
              (justificatif.isConfirming && !justificatif.canConfirm)
                  ? 'DISABLED'
                  : 'PRIMARY'
              }
              onClick={handleSubmit}
            >
              Confirmer
            </Button>
          </>
        )}
      </ModalFooter>
    </>
  );
};

export default ModalBonusesAndAllowancesRestaurantTicket;
